import { Link } from "@tanstack/react-router"
import { CheckCheck, ChevronLeft, ChevronRight, Inbox, RotateCw, Undo2 } from "lucide-react"
import { useMemo, useState } from "react"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PageHeader as ContentHeader } from "@/components/ui/page-header"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import type { Schemas } from "@/lib/api"
import { refusalOfError } from "@/lib/api"
import { type MessageKey, useFormatNumber, useT } from "@/lib/i18n"
import { stringifyExact } from "@/lib/lossless"
import { COMMAND_TASK_TYPE, type TaskOperation, useObjects, useTaskOperation } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { asString, isRecord } from "@/lib/utils"

export interface CommandTask {
  readonly id: string
  readonly status: string
  readonly open: boolean
  readonly actionId: string | undefined
  readonly objectType: string | undefined
  readonly objectKey: string | undefined
  readonly error: string | undefined
  readonly resolution: string | undefined
  readonly resolvedBy: string | undefined
  readonly recoveryRetryUsed: boolean
  readonly needsReconciliation: boolean
  readonly version: number
}

function errorText(error: unknown): string | undefined {
  if (error === null || error === undefined) return undefined
  if (typeof error === "string") return error
  if (isRecord(error))
    return asString(error.message) ?? asString(error.error) ?? asString(error.code) ?? stringifyExact(error)
  return stringifyExact(error)
}

export function toCommandTask(o: Schemas["ObjectResponse"]): CommandTask {
  const v = o.values as Record<string, unknown>
  const status = asString(v.status) ?? "unknown"
  return {
    id: o.key,
    status,
    // Only a terminal failure awaits a decision; dispatching/retrying tasks are in flight.
    open: status === "failed",
    actionId: asString(v.actionId),
    objectType: asString(v.objectType),
    objectKey: asString(v.objectKey),
    error: errorText(v.error),
    resolution: asString(v.resolution),
    resolvedBy: asString(v.resolvedBy),
    recoveryRetryUsed: v.recoveryRetryUsed === true,
    needsReconciliation: v.needsReconciliation === true,
    version: o.version,
  }
}

const CONSEQUENCE: Record<
  TaskOperation,
  { label: MessageKey; confirm: MessageKey; body: MessageKey; groupLabel: MessageKey }
> = {
  acknowledge: {
    label: "inbox.acknowledge",
    confirm: "inbox.acknowledgeConfirm",
    body: "inbox.acknowledgeConsequence",
    groupLabel: "inbox.confirmAcknowledge",
  },
  release: {
    label: "inbox.release",
    confirm: "inbox.releaseConfirm",
    body: "inbox.releaseConsequence",
    groupLabel: "inbox.confirmRelease",
  },
}

/** The cell's task states and resolutions, in the operator's language; an unknown one shows as the cell wrote it. */
const STATUS_LABEL: Readonly<Record<string, MessageKey>> = {
  failed: "inbox.status.failed",
  dispatching: "inbox.status.dispatching",
  retrying: "inbox.status.retrying",
  resolved: "inbox.status.resolved",
  acknowledged: "inbox.status.acknowledged",
  released: "inbox.status.released",
}
function statusLabel(t: ReturnType<typeof useT>, value: string): string {
  return Object.hasOwn(STATUS_LABEL, value) ? t(STATUS_LABEL[value] as MessageKey) : value
}

function TaskRow({ task }: { task: CommandTask }) {
  const t = useT()
  const scope = useScope()
  const op = useTaskOperation(scope)
  const [pending, setPending] = useState<TaskOperation | null>(null)
  const refusal = refusalOfError(op.error)
  return (
    <li className="px-5 py-3.5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 font-mono text-sm break-all text-foreground">
              {task.actionId ?? t("inbox.unattributedCommand")}
            </code>
            {task.open ? (
              <Badge tone="danger">{statusLabel(t, "failed")}</Badge>
            ) : task.status === "resolved" ? (
              <Badge tone="success">{statusLabel(t, task.resolution ?? "resolved")}</Badge>
            ) : (
              <Badge>{statusLabel(t, task.status)}</Badge>
            )}
            {task.needsReconciliation ? <Badge tone="warning">{t("inbox.needsReconciliation")}</Badge> : null}
            {task.recoveryRetryUsed ? <Badge>{t("inbox.retryUsed")}</Badge> : null}
          </div>
          {task.objectType && task.objectKey ? (
            <Link
              to="/$orgId/$environmentId/objects/$objectType/$key"
              params={{ ...scope, objectType: task.objectType, key: task.objectKey }}
              className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
            >
              {task.objectType} / {task.objectKey} <ChevronRight className="rtl-mirror size-3" aria-hidden />
            </Link>
          ) : null}
          {task.error ? (
            <p
              dir="auto"
              className="mt-1.5 line-clamp-2 max-w-[80ch] font-mono text-2xs break-words text-destructive/90"
            >
              {task.error}
            </p>
          ) : null}
          {!task.open && task.resolvedBy ? (
            <p className="mt-1 text-xs text-subtle-foreground">{t("inbox.resolvedBy", { name: task.resolvedBy })}</p>
          ) : null}
        </div>
        {task.open ? (
          <div className="flex shrink-0 gap-1.5">
            <Button
              size="sm"
              onClick={() => setPending("acknowledge")}
              disabled={op.isPending}
              aria-pressed={pending === "acknowledge"}
            >
              <CheckCheck /> {t(CONSEQUENCE.acknowledge.label)}
            </Button>
            <Button
              size="sm"
              onClick={() => setPending("release")}
              disabled={op.isPending || task.recoveryRetryUsed}
              aria-pressed={pending === "release"}
              title={task.recoveryRetryUsed ? t("inbox.recoveryRetryUsed") : undefined}
            >
              <Undo2 className="rtl-mirror" /> {t(CONSEQUENCE.release.label)}
            </Button>
          </div>
        ) : null}
      </div>
      {pending ? (
        <div
          role="group"
          aria-label={t(CONSEQUENCE[pending].groupLabel)}
          className="mt-3 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-md border border-border bg-card px-3 py-2"
        >
          <p className="min-w-0 flex-1 basis-full text-xs text-muted-foreground sm:basis-0">
            {t(CONSEQUENCE[pending].body)}
          </p>
          <Button size="sm" variant="ghost" onClick={() => setPending(null)}>
            {t("common.cancel")}
          </Button>
          <Button
            size="sm"
            variant={pending === "release" ? "primary" : "destructive"}
            disabled={op.isPending}
            onClick={() => op.mutate({ id: task.id, operation: pending }, { onSuccess: () => setPending(null) })}
          >
            {op.isPending ? t("common.working") : t(CONSEQUENCE[pending].confirm)}
          </Button>
        </div>
      ) : null}
      {op.isError ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {refusal
            ? `${refusal.refusal.message} — ${refusal.refusal.explanation} (${refusal.refusal.code} · ${refusal.refusal.category})`
            : op.error instanceof Error
              ? op.error.message
              : String(op.error)}
        </p>
      ) : null}
    </li>
  )
}

const INBOX_PAGE = 100

export function InboxPage() {
  const t = useT()
  const formatNumber = useFormatNumber()
  const scope = useScope()
  // Bounded pages over the task list; the opaque cursor only goes forward, so keep a trail.
  const [trail, setTrail] = useState<(string | null)[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const page = useObjects(scope, COMMAND_TASK_TYPE, cursor, INBOX_PAGE)
  const [view, setView] = useState<"open" | "resolved">("open")
  const tasks = useMemo(() => page.data?.items.map(toCommandTask) ?? [], [page.data])
  const open = tasks.filter((t) => t.open)
  const resolved = tasks.filter((t) => !t.open)
  const shown = view === "open" ? open : resolved
  const more = Boolean(page.data?.nextCursor)
  const paged = more || trail.length > 0
  return (
    <>
      <PageHeader
        crumbs={[t("nav.inbox")]}
        actions={
          <Button
            size="icon"
            variant="ghost"
            onClick={() => void page.refetch()}
            disabled={page.isFetching}
            aria-label={t("common.refresh")}
          >
            <RotateCw className={page.isFetching ? "animate-spin" : undefined} />
          </Button>
        }
      />
      <div className="flex-1 overflow-y-auto">
        <Screen width="board" className="px-4 py-6 md:px-6">
          <ContentHeader description={t("inbox.description")} />
          <div
            role="tablist"
            aria-label={t("inbox.taskState")}
            className="mt-6 flex gap-4 border-b border-border-subtle"
          >
            {(["open", "resolved"] as const).map((v) => (
              <button
                key={v}
                role="tab"
                type="button"
                aria-selected={view === v}
                onClick={() => setView(v)}
                className={`-mb-px border-b-2 pb-2 text-sm transition-colors ${view === v ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              >
                {v === "open" ? t("inbox.needsDecision") : t("inbox.resolvedInFlight")}{" "}
                <span className="text-xs text-subtle-foreground tabular">
                  {formatNumber(v === "open" ? open.length : resolved.length)}
                </span>
              </button>
            ))}
            {paged ? (
              <span className="ms-auto self-center pb-2 text-xs text-subtle-foreground tabular">
                {t("inbox.page", { page: trail.length + 1 })}
              </span>
            ) : null}
          </div>
          {page.isPending ? (
            <TableSkeleton rows={4} columns={2} />
          ) : page.isError ? (
            <ErrorState error={page.error} what={t("inbox.commandTasks")} onRetry={() => void page.refetch()} inline />
          ) : shown.length === 0 ? (
            <EmptyState
              icon={<Inbox />}
              title={
                view === "open"
                  ? t(paged ? "inbox.nothingNeedsDecisionPage" : "inbox.nothingNeedsDecision")
                  : t(paged ? "inbox.nothingResolvedPage" : "inbox.nothingResolved")
              }
            >
              <p>
                {more
                  ? t("inbox.moreTasks")
                  : view === "open"
                    ? t("inbox.allResolved")
                    : t("inbox.resolvedDescription")}
              </p>
            </EmptyState>
          ) : (
            <Section bordered>
              <ul className="divide-y divide-border">
                {shown.map((t) => (
                  <TaskRow key={t.id} task={t} />
                ))}
              </ul>
            </Section>
          )}
          {paged ? (
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <span>{t("inbox.pageCounts", { count: INBOX_PAGE })}</span>
              <div className="ms-auto flex gap-1.5">
                <Button
                  size="sm"
                  disabled={trail.length === 0 || page.isFetching}
                  onClick={() => {
                    setCursor(trail[trail.length - 1] ?? null)
                    setTrail((t) => t.slice(0, -1))
                  }}
                >
                  <ChevronLeft className="rtl-mirror" /> {t("common.previous")}
                </Button>
                <Button
                  size="sm"
                  disabled={!more || page.isFetching}
                  onClick={() => {
                    const next = page.data?.nextCursor
                    if (!next) return
                    setTrail((t) => [...t, cursor])
                    setCursor(next)
                  }}
                >
                  {t("common.next")} <ChevronRight className="rtl-mirror" />
                </Button>
              </div>
            </div>
          ) : null}
          <p className="mt-6 text-xs text-subtle-foreground">{t("inbox.humanApprovalsDeferred")}</p>
        </Screen>
      </div>
    </>
  )
}
